import React, { useState, useEffect } from 'react';
import { ActionConfigPopup } from '../../../actions';
import * as Icons from 'lucide-react';
import { useAppStore } from '../../../../store/useAppStore';
import { fetchAppPagesWithVersions } from '../../../../services/firestoreService';
import { getLayoutSlots } from '../../../../config/layouts';
import { getSlotIcon, getSlotDefinition } from '../../../../config/slotDefinitions';

export const NavigationForm = ({ component, onUpdate, layoutId }) => {
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [pages, setPages] = useState([]);
  const { appName } = useAppStore();

  // Get available slots for the current layout
  const allSlots = layoutId ? getLayoutSlots(layoutId) : [];
  // Filter slots to only show those that allow multiple components or have maxComponents > 1
  const availableSlots = allSlots.filter(slot => {
    const slotDef = getSlotDefinition(slot.id);
    return slotDef?.allowMultiple === true || (slotDef?.maxComponents || 1) > 1;
  });
  
  // Find default body slot (prefer 'body', fallback to first available slot)
  const defaultSlotId = availableSlots.find(s => s.id === 'body')?.id || 
                       availableSlots.find(s => s.id === 'content')?.id || 
                       (availableSlots.length > 0 ? availableSlots[0].id : 'body');

  // Load pages for the app
  useEffect(() => {
    const loadPages = async () => {
      if (!appName) return;
      try {
        const fetchedPages = await fetchAppPagesWithVersions(appName);
        setPages(fetchedPages);
      } catch (error) {
        console.error('Failed to load pages:', error);
      }
    };
    loadPages();
  }, [appName]);

  const handleChange = (field, value) => {
    onUpdate(component.id, {
      specificAttrs: { ...component.specificAttrs, [field]: value }
    });
  };

  const { specificAttrs } = component;
  const {
    navigationType = 'horizontal-navbar',
    position = 'top-sticky',
    scope = 'this-page-only',
    menuItems = [],
    backgroundColor = '#1F2937',
    textColor = '#FFFFFF',
    hoverColor = '#3B82F6'
  } = specificAttrs;

  // Add new menu item
  const handleAddMenuItem = () => {
    const newItem = {
      id: `item_${Date.now()}`,
      label: 'New Item',
      icon: 'Circle',
      badge: '',
      targetPage: '',
      openInSlot: defaultSlotId, // Default to body/content slot
      clearSlotBeforeLoad: true, // Default to clearing slot
      openInNewTab: false,
      action: null
    };
    handleChange('menuItems', [...menuItems, newItem]);
  };

  // Remove menu item
  const handleRemoveMenuItem = (index) => {
    const updatedItems = menuItems.filter((_, i) => i !== index);
    handleChange('menuItems', updatedItems);
  };

  // Update menu item field
  const handleMenuItemChange = (index, field, value) => {
    const updatedItems = [...menuItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    handleChange('menuItems', updatedItems);
  };

  // Open action configuration popup
  const handleConfigureAction = (index) => {
    setEditingItemIndex(index);
    setShowActionPopup(true);
  };

  // Save action to menu item
  const handleActionSelect = (actionConfig) => {
    if (editingItemIndex !== null) {
      handleMenuItemChange(editingItemIndex, 'action', actionConfig);
    }
    setShowActionPopup(false);
    setEditingItemIndex(null);
  };

  // Available icons (subset of Lucide icons)
  const availableIcons = [
    'Home', 'Info', 'Mail', 'Phone', 'User', 'Settings', 
    'ShoppingCart', 'Heart', 'Star', 'Search', 'Menu',
    'ChevronRight', 'ChevronDown', 'Circle', 'Square',
    'Package', 'FileText', 'Image', 'Video', 'Music'
  ];

  return (
    <div style={{ display: 'grid', gap: '16px', padding: '4px' }}>
      {/* Section 1: Basic Settings */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Basic Settings
        </h4>

        {/* Navigation Type */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Menu Style
          </label>
          <select
            value={navigationType}
            onChange={(e) => handleChange('navigationType', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="horizontal-navbar">Horizontal Navbar (Top)</option>
            <option value="vertical-sidebar">Vertical Sidebar (Left)</option>
            <option value="footer-navigation">Footer Navigation (Bottom)</option>
            <option value="mobile-bottom-bar">Mobile Bottom Bar</option>
          </select>
        </div>

        {/* Position */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Position
          </label>
          <select
            value={position}
            onChange={(e) => handleChange('position', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="top-fixed">Top (Fixed)</option>
            <option value="top-sticky">Top (Sticky - scrolls with page)</option>
            <option value="bottom">Bottom</option>
            <option value="left-side">Left Side</option>
          </select>
        </div>

        {/* Scope */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '6px', 
            color: '#4B5563' 
          }}>
            Show on
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="scope"
                value="this-page-only"
                checked={scope === 'this-page-only'}
                onChange={(e) => handleChange('scope', e.target.value)}
              />
              This page only
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="scope"
                value="all-pages"
                checked={scope === 'all-pages'}
                onChange={(e) => handleChange('scope', e.target.value)}
              />
              All pages
            </label>
          </div>
        </div>
      </div>

      {/* Section 2: Menu Items */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Menu Items
        </h4>

        {menuItems.length === 0 && (
          <p style={{ 
            fontSize: '12px', 
            color: '#6B7280', 
            fontStyle: 'italic',
            marginBottom: '12px'
          }}>
            No menu items yet. Click "Add Menu Item" to create your first link.
          </p>
        )}

        {menuItems.map((item, index) => (
          <div 
            key={item.id}
            style={{ 
              padding: '12px',
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              border: '1px solid #E5E7EB',
              marginBottom: '8px'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '600', 
                color: '#374151' 
              }}>
                Item {index + 1}
              </span>
              <button
                onClick={() => handleRemoveMenuItem(index)}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  color: '#DC2626',
                  backgroundColor: '#FEE2E2',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Remove ✕
              </button>
            </div>

            {/* Label */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Link Text
              </label>
              <input
                type="text"
                value={item.label}
                onChange={(e) => handleMenuItemChange(index, 'label', e.target.value)}
                placeholder="e.g., Home, About, Contact"
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              />
            </div>

            {/* Icon */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Icon
              </label>
              <select
                value={item.icon}
                onChange={(e) => handleMenuItemChange(index, 'icon', e.target.value)}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                {availableIcons.map(iconName => {
                  const IconComponent = Icons[iconName];
                  return (
                    <option key={iconName} value={iconName}>
                      {iconName}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Badge */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Badge (optional)
              </label>
              <input
                type="text"
                value={item.badge || ''}
                onChange={(e) => handleMenuItemChange(index, 'badge', e.target.value)}
                placeholder="e.g., New, 5, Beta"
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              />
              <p style={{ 
                fontSize: '9px', 
                color: '#9CA3AF', 
                marginTop: '2px',
                fontStyle: 'italic'
              }}>
                Small label shown next to the link
              </p>
            </div>

            {/* Target Page */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Go to Page *
              </label>
              <select
                value={item.targetPage || ''}
                onChange={(e) => handleMenuItemChange(index, 'targetPage', e.target.value)}
                style={{
                  width: '100%',
                  padding: '5px 8px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF'
                }}
              >
                <option value="">-- Choose a page --</option>
                {pages.map(page => (
                  <option key={page.id || page.name} value={page.pageName || page.name || page.id}>
                    {page.pageName || page.name || page.id}
                  </option>
                ))}
              </select>
            </div>

            {/* Open Page In */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Show in
              </label>
              {availableSlots.length > 0 ? (
                <>
                  <select
                    value={item.openInSlot || defaultSlotId}
                    onChange={(e) => handleMenuItemChange(index, 'openInSlot', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '5px 8px',
                      fontSize: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF'
                    }}
                  >
                    {availableSlots.map(slot => {
                      const slotDef = getSlotDefinition(slot.id);
                      const slotIcon = getSlotIcon(slot.id);
                      const isDefault = slot.id === defaultSlotId;
                      return (
                        <option key={slot.id} value={slot.id}>
                          {slotIcon} {slot.name || slot.id} {isDefault ? '(Default)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {(() => {
                    const selectedSlot = availableSlots.find(s => s.id === (item.openInSlot || defaultSlotId));
                    const slotDef = selectedSlot ? getSlotDefinition(selectedSlot.id) : null;
                    const isDefault = (item.openInSlot || defaultSlotId) === defaultSlotId;
                    return (
                      <p style={{ 
                        fontSize: '9px', 
                        color: isDefault ? '#10B981' : '#F59E0B', 
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        {isDefault 
                          ? `✓ Shows page content here (${selectedSlot?.name || 'Main Area'})`
                          : `⚠️ Will replace content in ${selectedSlot?.name || 'this area'}`}
                      </p>
                    );
                  })()}
                </>
              ) : (
                <p style={{ 
                  fontSize: '10px', 
                  color: '#9CA3AF', 
                  fontStyle: 'italic',
                  padding: '8px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '4px'
                }}>
                  No available slots found. Please select a layout first.
                </p>
              )}
            </div>

            {/* Clear Slot Before Loading */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '11px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={item.clearSlotBeforeLoad !== false}
                  onChange={(e) => handleMenuItemChange(index, 'clearSlotBeforeLoad', e.target.checked)}
                />
                <span style={{ color: '#6B7280' }}>Replace existing content</span>
              </label>
              <p style={{ 
                fontSize: '9px', 
                color: '#9CA3AF', 
                marginTop: '2px',
                fontStyle: 'italic',
                marginLeft: '20px'
              }}>
                {item.clearSlotBeforeLoad !== false 
                  ? '✓ New page will replace what\'s currently shown'
                  : '⚠️ New page will be added below existing content'}
              </p>
            </div>

            {/* Open in New Tab */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '11px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={item.openInNewTab || false}
                  onChange={(e) => handleMenuItemChange(index, 'openInNewTab', e.target.checked)}
                />
                <span style={{ color: '#6B7280' }}>Open in new window</span>
              </label>
            </div>

            {/* Action */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '11px', 
                fontWeight: '500', 
                marginBottom: '3px', 
                color: '#6B7280' 
              }}>
                Advanced Actions (optional)
              </label>
              <button
                onClick={() => handleConfigureAction(index)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '12px',
                  color: '#FFFFFF',
                  backgroundColor: item.action ? '#10B981' : '#3B82F6',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {item.action ? '✓ Action Set' : 'Add Action'}
              </button>
              {item.action && (
                <p style={{ 
                  fontSize: '10px', 
                  color: '#6B7280', 
                  marginTop: '4px',
                  fontStyle: 'italic'
                }}>
                  {item.action.actionType || 'Action configured'}
                </p>
              )}
              <p style={{ 
                fontSize: '9px', 
                color: '#9CA3AF', 
                marginTop: '4px',
                fontStyle: 'italic'
              }}>
                Add extra actions like showing messages or opening popups
              </p>
            </div>
          </div>
        ))}

        {/* Add Menu Item Button */}
        <button
          onClick={handleAddMenuItem}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '13px',
            color: '#3B82F6',
            backgroundColor: '#EFF6FF',
            border: '1px dashed #3B82F6',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          + Add Menu Item
        </button>
      </div>

      {/* Section 3: Styling */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Colors & Style
        </h4>

        <div style={{ display: 'grid', gap: '10px' }}>
          {/* Background Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Background Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Text Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={textColor}
                onChange={(e) => handleChange('textColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={textColor}
                onChange={(e) => handleChange('textColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>

          {/* Hover Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Hover Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={hoverColor}
                onChange={(e) => handleChange('hoverColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={hoverColor}
                onChange={(e) => handleChange('hoverColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Configuration Popup */}
      {showActionPopup && (
        <ActionConfigPopup
          isOpen={showActionPopup}
          onClose={() => {
            setShowActionPopup(false);
            setEditingItemIndex(null);
          }}
          onActionSelect={handleActionSelect}
        />
      )}
    </div>
  );
};






